import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PublishIcon from '@mui/icons-material/Publish';
import NoteAddIcon from '@mui/icons-material/NoteAddOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { VersionFacts } from './VersionFacts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import {
  formatCivilDate,
  resolveFormattingLocale,
  useCreateDraftVersion,
  useVersionsWithDrafts,
  useViewLevel,
} from '@eis/core';
import {
  EmptyState,
  EntityHeader,
  ErrorState,
  PageLayout,
  RequirePermission,
  StatusChip,
  cardSurfaceSx,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import {
  tarifVersionManifestSchema,
  useGeneration,
  usePublishTarifVersion,
  useValidateTarifVersion,
} from '../api/tarifs';
import { manifestFormFrom } from '../model/manifest-form';
import { bareEnum, versionNameOf } from '../model/types';
import { lifecycleOf } from '../model/version-list';
import { DraftVersionEditor } from '../../tarif-authoring/ui/DraftVersionEditor';
import { VersionStatusActions } from '../../tarif-authoring/ui/VersionStatusActions';
import { ProvisionalRefMapField } from '../../tarif-authoring/ui/ProvisionalRefMapField';
import { TarifRefsField } from '../../tarif-authoring/ui/TarifRefsField';
import { PinnedDeckungCard } from '../../tarif-authoring/ui/deckung/overview/PinnedDeckungCard';
import { ManifestLibrary } from './ManifestLibrary';
import { PinnedLifecycle } from './PinnedLifecycle';
import { PublishWizard } from './PublishWizard';
import { VersionList } from './VersionList';

/**
 * One generation: its versions, and what each of them pinned.
 *
 * The list is grouped by major with the minors nested below in numeric order (SDD-016 D-V10), and
 * it includes the open DRAFT versions with their issue counts. Which version is IN FORCE is a
 * separate fact — the generation's `activeVersionErn` — marked rather than assumed: the newest
 * version is not necessarily the active one. The status of the selected version is changed here
 * (D-V3), and a draft opens in the draft editor (D-V4).
 */
export function GenerationDetailPage({
  slug,
  generation,
}: {
  readonly slug: string;
  readonly generation: string;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const { isAdvanced } = useViewLevel();
  const generationQuery = useGeneration(slug, generation);
  const versionsQuery = useVersionsWithDrafts({ owner: 'tarif', slug, generation });
  /** The selected version's ERN — a draft has no number yet, so the ERN is the stable key. */
  const [selected, setSelected] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<string | undefined>(undefined);
  const createDraft = useCreateDraftVersion();
  const [publishing, setPublishing] = useState(false);
  /**
   * The version the OPEN wizard was prefilled from, frozen for as long as it is open.
   *
   * The wizard takes its prefill as an initial value, so it has to be remounted when the version
   * it copies changes — but publishing CREATES a version, which changes `current` underneath the
   * dialog and would remount it at the exact moment it is showing what it wrote. Freezing the key
   * on open is what keeps "Veröffentlicht als …" on screen; the dialog is modal, so nothing else
   * can change the selection while it is up.
   */
  const [publishKey, setPublishKey] = useState('none');
  const validate = useValidateTarifVersion();
  const publish = usePublishTarifVersion();

  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data]);
  const published = versions.filter((version) => lifecycleOf(version) !== 'DRAFT');
  const active = generationQuery.data?.generation;

  // Default to the version in force rather than to the newest: "what is my product doing right
  // now" is the question this screen is opened with.
  const current =
    versions.find((version) => version.ern === selected) ??
    versions.find((version) => lifecycleOf(version, active) === 'ACTIVE') ??
    published.at(-1);
  const currentLifecycle = current === undefined ? undefined : lifecycleOf(current, active);

  if (generationQuery.isError || versionsQuery.isError) {
    return (
      <PageLayout>
        <ErrorState
          description={t('tarifs.error.generation')}
          details={(generationQuery.error ?? versionsQuery.error)?.message}
          onRetry={() => {
            void generationQuery.refetch();
            void versionsQuery.refetch();
          }}
        />
      </PageLayout>
    );
  }

  if (generationQuery.data === undefined) {
    return (
      <PageLayout header={<Skeleton variant="text" width={320} height={44} />}>
        <Skeleton variant="rounded" height={260} />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      header={
        <EntityHeader
          title={t('tarifs.generations.label', { name: active?.name ?? generation })}
          subtitle={active?.ern}
          icon={<LayersOutlinedIcon />}
          backTo={{
            label: slug,
            href: `/insurance/tarifs/${slug}`,
            onNavigate: (event) => {
              event.preventDefault();
              void navigate({ href: `/insurance/tarifs/${slug}` });
            },
          }}
          meta={[
            {
              label: t('tarifs.generations.validFromLabel'),
              value:
                active?.validFrom === undefined ? '—' : formatCivilDate(active.validFrom, locale),
            },
            {
              label: t('tarifs.generations.activeVersionLabel'),
              value:
                active?.activeVersionErn === undefined || active.activeVersionErn === ''
                  ? '—'
                  : active.activeVersionErn.slice(active.activeVersionErn.lastIndexOf('/') + 1),
            },
          ]}
          actions={
            <RequirePermission anyOf={[p.products.publish]}>
              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                // A manifest is authored by editing the one in force, so the wizard cannot open
                // before there is one to copy — it would prefill an empty form and quietly
                // discard every pin the previous version made.
                disabled={versionsQuery.isPending}
                onClick={() => {
                  setPublishKey(current?.ern ?? 'none');
                  setPublishing(true);
                }}
              >
                {t('tarifs.publish.action')}
              </Button>
            </RequirePermission>
          }
        />
      }
    >
      {/* The rule a configurator asks about first, and the answer is no (SSOT §5.3). */}
      <Alert severity="info">{t('tarifs.generations.inForceHint')}</Alert>

      {versionsQuery.isPending ? (
        <Skeleton variant="rounded" height={200} />
      ) : versions.length === 0 ? (
        <EmptyState description={t('tarifs.versions.empty')} />
      ) : (
        /*
          Design v2's two-column version screen (prompt 24 Phase 24.4): the versions on the left,
          the selected one — its header, its status menu, the assets it pins — on the right.
        */
        <Grid container spacing={2.5} sx={{ alignItems: 'flex-start' }}>
          <Grid size={{ xs: 12, md: 5, lg: 4 }}>
            <Paper
              variant="outlined"
              sx={(theme) => ({ ...cardSurfaceSx(theme), overflow: 'hidden' })}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}
              >
                <Typography variant="h4" component="h2" id="version-switcher">
                  {t('tarifs.versions.listTitle', { generation: active?.name ?? generation })}
                </Typography>
                <RequirePermission anyOf={[p.products.update]}>
                  <Button
                    variant="contained"
                    startIcon={<NoteAddIcon />}
                    // A draft starts from the version on screen (D-V4) — its manifest, `basedOn` it.
                    disabled={createDraft.isPending}
                    onClick={() => {
                      const basedOnErn =
                        current !== undefined && currentLifecycle !== 'DRAFT'
                          ? current.ern
                          : undefined;
                      createDraft.mutate(
                        {
                          owner: 'tarif',
                          slug,
                          generation,
                          ...(basedOnErn === undefined ? {} : { basedOnErn }),
                        },
                        {
                          onSuccess: (draft) => {
                            setSelected(draft.ern ?? null);
                            setEditingDraft(draft.draftId);
                          },
                        },
                      );
                    }}
                  >
                    {t('authoring.draft.new')}
                  </Button>
                </RequirePermission>
              </Stack>
              <Divider />
              <VersionList
                versions={versions}
                generation={active}
                selectedErn={current?.ern}
                onSelect={setSelected}
              />
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 7, lg: 8 }}>
            {current !== undefined && (
              <Stack spacing={2.5}>
                <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      spacing={2}
                      useFlexGap
                      sx={{
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          useFlexGap
                          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                        >
                          <Typography variant="h3" component="h2">
                            {`${slug} / ${active?.name ?? generation} / ${
                              currentLifecycle === 'DRAFT'
                                ? t('tarifs.versions.draftLabel')
                                : versionNameOf(current)
                            }`}
                          </Typography>
                          <StatusChip
                            status={currentLifecycle ?? 'PUBLISHED'}
                            // The version in force says so in the words the operator asks with.
                            label={
                              currentLifecycle === 'ACTIVE'
                                ? t('tarifs.versions.inForce')
                                : t(
                                    `tarifs.versions.statusValue.${currentLifecycle ?? 'PUBLISHED'}` as never,
                                  )
                            }
                          />
                        </Stack>
                        <VersionFacts
                          facts={[
                            [t('tarifs.versions.generation'), active?.name ?? generation],
                            [
                              t('tarifs.generations.validFromLabel'),
                              active?.validFrom === undefined
                                ? '—'
                                : formatCivilDate(active.validFrom, locale),
                            ],
                            [
                              t('tarifs.versions.kind'),
                              t(
                                `tarifs.releaseType.${bareEnum(current.releaseType, 'RELEASE_TYPE_') || 'UNSPECIFIED'}` as never,
                              ),
                            ],
                            [
                              t('tarifs.versions.basedOn'),
                              current.basedOnErn === undefined || current.basedOnErn === ''
                                ? t('tarifs.versions.noBaseline')
                                : current.basedOnErn.slice(current.basedOnErn.lastIndexOf('/') + 1),
                            ],
                            ...(isAdvanced
                              ? ([
                                  [t('tarifs.versions.ern'), current.ern ?? '—'],
                                  [t('tarifs.versions.publishedBy'), current.publishedBy ?? '—'],
                                ] as const)
                              : []),
                          ]}
                        />
                      </Box>
                      {/*
                        Which version is active — and which is retired — is a DECISION, taken here
                        (prompt 14.5, SDD-016 D-V3), gated with `publish`: it decides what every new
                        policy pins, the same weight as freezing an artifact.
                      */}
                      <VersionStatusActions
                        owner="tarif"
                        slug={slug}
                        generation={generation}
                        version={versionNameOf(current)}
                        lifecycle={currentLifecycle ?? 'PUBLISHED'}
                        onEditDraft={() => {
                          setEditingDraft(current.draftId);
                        }}
                      />
                    </Stack>
                    <ManifestLibrary embedded manifest={current.manifest} digest={current.digest} />
                  </Stack>
                </Paper>
                {/* The lead status machine this version pins, or the platform default (SDD-013 §6). */}
                <PinnedLifecycle slug={slug} manifest={current.manifest} />
                {/* The variations and claims configuration this version pins (SDD-018 §5). */}
                <PinnedDeckungCard
                  owner="tarif"
                  ern={current.ern ?? ''}
                  manifest={current.manifest}
                  draft={currentLifecycle === 'DRAFT'}
                />
              </Stack>
            )}
          </Grid>
        </Grid>
      )}

      <RequirePermission anyOf={[p.products.publish]}>
        <PublishWizard
          // The wizard takes its prefill as an INITIAL value, so it has to be remounted when the
          // version it was copied from changes. Keying on that version's ERN is what makes
          // "switch to v1, then publish" start from v1's manifest instead of from v2's — and the
          // dialog is modal, so this can never discard an edit in progress.
          key={publishKey}
          title={t('tarifs.publish.title', { generation })}
          open={publishing}
          // Prefilled from the version in force: a manifest is authored by taking the last one
          // and moving a pin, and the release type resets to PATCH because that is what moving
          // one pin is.
          initialValues={manifestFormFrom(current?.manifest, 'PATCH')}
          baselines={published
            .map((version) => version.ern)
            .filter((ern): ern is string => ern !== undefined && ern !== '')}
          schema={tarifVersionManifestSchema}
          // Pickers over this tarif's OWN published assets: the console read them one screen ago
          // to draw the asset lists, and asking for an ERN it could look up is a transcription
          // error the gate would refuse with a 409 several screens later.
          renderRefs={(props) => <TarifRefsField tarifSlug={slug} {...props} />}
          // Documents, workflows and settings get a PROVISIONAL dropdown over the refs this
          // tarif's own published versions already pin — documentservice and workflowservice do
          // not exist, and the asset registry cannot enumerate (OPEN-QUESTIONS OQ-14). The
          // textarea stays as the escape hatch.
          renderBlock={(props) => (
            <ProvisionalRefMapField
              {...props}
              manifests={published.map((version) => version.manifest)}
            />
          )}
          validate={validate}
          publish={publish}
          variablesOf={(manifest, { activate: activateNow }) => ({
            tarifSlug: slug,
            generation,
            manifest,
            activate: activateNow,
          })}
          // The dry run drops the generation's parked ETag (see useValidateTarifVersion), so the
          // publish action waits for the re-read rather than sending a request with no
          // precondition and turning a normal flow into a 428.
          preconditionReady={!generationQuery.isFetching}
          onClose={() => {
            setPublishing(false);
          }}
          onPublished={(ern) => {
            if (ern !== undefined && ern !== '') setSelected(ern);
          }}
        />
      </RequirePermission>

      <DraftVersionEditor
        owner="tarif"
        slug={slug}
        generation={generation}
        draftId={editingDraft}
        open={editingDraft !== undefined}
        onClose={() => {
          setEditingDraft(undefined);
        }}
        onPublished={(version) => {
          setSelected(version.ern ?? null);
        }}
      />
    </PageLayout>
  );
}
